import React, { useCallback, useEffect, useState } from "react";
import { SelectOption, Button } from "../../../ui";
import { CustomAction, TableColumn } from "../../../core/interfaces/table";
import { DateFormatEnums } from "../../../core/utils/date-format";
import { DataTable } from "../../../ui/components/Datatable";
import { useModal } from "../../../core/hooks/useModal";
import { useDebounce } from "../../../core/hooks/useDebounce";
import { appService } from "../../../core/services/app";
import { ResponseType } from "../../../core/interfaces/IResponse";
import { IEntity } from "../../../core/interfaces/IEntity";

const ListPendingEntitiesModal: React.FC = () => {
  const [entities, setEntities] = useState<IEntity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const {  modalRef } = useModal();
  const sortOptions: SelectOption[] = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
  ];

  const initialCap = (str: string): string => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const columns: TableColumn[] = [
    {
      header: "Logo",
      value: (item: IEntity) => item.entity?.logo,
      type: "image",
      imageConfig: {
        size: "sm",
        altText: (item: IEntity) => item.entity?.name,
      },
    },
    {
      header: "Entity Name",
      value: (item: IEntity) => [item.entity?.name, item.entity?.address],
      type: "column",
    },
    {
      header: "Contact",
      value: (item: IEntity) => [item.entity?.email, item.entity?.phone_number],
      type: "column",
    },
    {
      header: "Status",
      value: (item: IEntity) => initialCap(item.entity?.approved ? "approved" : "pending"),
      type: "status",
      statusClasses: (item: IEntity) => {
        switch (item.entity?.approved ? "approved" : "pending") {
          case "pending":
            return "bg-info-10 text-info";
          case "approved":
            return "bg-success-10 text-success";
          default:
            return "bg-danger-10 text-danger";
        }
      },
    },
    {
      header: "Submission Date",
      value: (item: IEntity) => item.entity?.created_at,
      type: "date",
      format: DateFormatEnums.DATE_TIME_SHORT,
    },
  ];

  const getPendingEntities = useCallback(
    async (page: number, search: string): Promise<void> => {
      setLoading(true);
      try {
       
        const res = await appService.getPendingEntities(page, search);

        if (res.response === ResponseType.SUCCESSFUL) {
          setEntities(res.results);
          setTotalCount(res.count);
        }
      } catch (err: any) {
        console.error("Error fetching pending entities:", err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    getPendingEntities(currentPage, debouncedSearchTerm);
  }, [getPendingEntities, currentPage, debouncedSearchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const getCustomActions = (item: IEntity): CustomAction[] => [
    {
      title: "Edit Entity",
      handler: () =>
        modalRef!.close({ success: "edit_entity", data: item.entity }),
      icon: "add-line",
      classes: "text-success hover:bg-success-50",
    },
  ];

  return (
    <div className="flex flex-col h-full w-full mx-auto">
      {/* Header (Modal Style) */}
      <div className="flex flex-row justify-between items-start mb-4 border-b border-border pb-4 sticky top-0 z-10 bg-card">
        <div className="flex flex-col">
          <h2 className="text-2xl text-text font-bold">
            Pending Entity Approvals
          </h2>
          <h4 className="text-md text-text-light mt-1">
            Review and manage entity registration requests.
          </h4>
        </div>
        <button
          onClick={() => modalRef!.dismiss()}
          className="w-8 h-8 rounded-full text-text-light hover:bg-background-50 transition-colors flex items-center justify-center"
          aria-label="Close modal"
        >
          <i className="ri-close-line text-xl"></i>
        </button>
      </div>

      {/* Scrollable Body - Contains DataTable */}
      <div className="flex-1 overflow-y-auto -m-4">
        <DataTable
          columns={columns}
          data={entities}
          loading={loading}
          sortOptions={sortOptions}
          customActions={getCustomActions}
          noDataMessage="No pending entity approvals found."
          page={currentPage}
          count={totalCount}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
        />
      </div>

      {/* Footer (Modal Style) */}
      <div className="flex justify-end items-center pt-4 h-14 border-t border-border mt-auto sticky bottom-0 z-10 bg-card">
        <Button onClick={() => modalRef!.dismiss()} variant="ghost">
          Close
        </Button>
      </div>
    </div>
  );
};

export default ListPendingEntitiesModal;
